package com.checkin.host

import androidx.compose.runtime.*
import com.checkin.host.api.ApiClient
import com.checkin.host.theme.AppColors
import com.checkin.host.theme.AppThemeHolder
import com.checkin.host.ui.auth.AuthRoute
import com.checkin.host.ui.auth.AuthScreen
import com.checkin.host.ui.auth.ForgotPasswordScreen
import com.checkin.host.ui.auth.ResetPasswordScreen
import com.checkin.host.ui.auth.VerifyEmailScreen
import com.checkin.host.ui.auth.VerifyPendingScreen
import com.checkin.host.ui.auth.authRouteFromWindow
import com.checkin.host.ui.auth.clearAuthQueryParams
import com.checkin.host.ui.auth.isEmailDeepLink
import com.checkin.host.ui.legal.LegalPageScreen
import com.checkin.shared.legal.LegalTexts
import com.checkin.host.ui.billing.SubscriptionScreen
import com.checkin.host.ui.billing.billingCheckoutReturnFromWindow
import com.checkin.host.ui.billing.clearBillingQueryParams
import com.checkin.host.ui.contracts.ContractsScreen
import com.checkin.host.ui.dashboard.DashboardScreen
import com.checkin.host.ui.layout.AppShell
import com.checkin.host.ui.layout.NavPage
import com.checkin.host.ui.layout.resetViewportAfterNavigation
import com.checkin.host.ui.profile.ProfileScreen
import com.checkin.host.ui.properties.PropertiesScreen
import com.checkin.host.ui.reservations.ReservationsScreen
import com.checkin.shared.dto.UserDto
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun HostApp() {
    // S'abonner au thème pour forcer la recomposition de toute l'app
    AppThemeHolder.modeState.value

    val windowAuthRoute = authRouteFromWindow()
    var authRoute by remember(windowAuthRoute) {
        mutableStateOf(windowAuthRoute ?: AuthRoute.Login)
    }
    var user by remember(windowAuthRoute) {
        mutableStateOf<UserDto?>(
            if (windowAuthRoute?.isEmailDeepLink() == true) {
                ApiClient.clearSession()
                null
            } else {
                ApiClient.loadUser()
            },
        )
    }
    val billingReturn = remember { billingCheckoutReturnFromWindow() }
    var currentPage by remember {
        mutableStateOf(if (billingReturn != null) NavPage.SUBSCRIPTION else NavPage.DASHBOARD)
    }
    var billingCheckoutSuccess by remember { mutableStateOf(billingReturn) }
    var sessionExpired by remember { mutableStateOf(false) }

    LaunchedEffect(windowAuthRoute) {
        if (windowAuthRoute?.isEmailDeepLink() == true) {
            ApiClient.clearSession()
            user = null
            authRoute = windowAuthRoute
        }
    }

    val activeAuthRoute = windowAuthRoute?.takeIf { it.isEmailDeepLink() } ?: authRoute
    val showAuthFlow = user == null || !ApiClient.isLoggedIn() || activeAuthRoute.isEmailDeepLink()

    Div({
        style {
            backgroundColor(AppColors.bg)
            color(AppColors.text)
            property("min-height", "100dvh")
        }
    }) {
        if (showAuthFlow) {
            when (val route = activeAuthRoute) {
                is AuthRoute.VerifyEmail -> VerifyEmailScreen(
                    token = route.token,
                    onAuthenticated = { authenticated ->
                        user = authenticated
                        authRoute = AuthRoute.Login
                    },
                    onBackToLogin = { authRoute = AuthRoute.Login },
                )
                is AuthRoute.ResetPassword -> ResetPasswordScreen(
                    token = route.token,
                    onDone = {
                        clearAuthQueryParams()
                        ApiClient.clearSession()
                        user = null
                        authRoute = AuthRoute.Login
                    },
                )
                is AuthRoute.VerifyPending -> VerifyPendingScreen(
                    email = route.email,
                    onBackToLogin = { authRoute = AuthRoute.Login },
                )
                AuthRoute.ForgotPassword -> ForgotPasswordScreen(
                    onBackToLogin = { authRoute = AuthRoute.Login },
                )
                is AuthRoute.LegalPrivacy -> LegalPageScreen(
                    document = LegalTexts.privacy(),
                    onBack = { authRoute = route.returnRoute },
                )
                is AuthRoute.LegalTerms -> LegalPageScreen(
                    document = LegalTexts.terms(),
                    onBack = { authRoute = route.returnRoute },
                )
                is AuthRoute.LegalDpa -> LegalPageScreen(
                    document = LegalTexts.dpa(),
                    onBack = { authRoute = route.returnRoute },
                )
                AuthRoute.Login, AuthRoute.Register -> AuthScreen(
                    route = route,
                    onRouteChange = { authRoute = it },
                    onAuthenticated = { authenticatedUser ->
                        user = authenticatedUser
                        sessionExpired = false
                        currentPage = NavPage.DASHBOARD
                        authRoute = AuthRoute.Login
                    },
                )
            }
        } else {
            LoggedInApp(
                user = user!!,
                currentPage = currentPage,
                billingCheckoutSuccess = billingCheckoutSuccess,
                sessionExpired = sessionExpired,
                onNavigate = { currentPage = it },
                onLogout = {
                    ApiClient.clearSession()
                    user = null
                },
                onUserUpdated = { user = it },
                onCheckoutReturnConsumed = { billingCheckoutSuccess = null },
            )
        }
    }
}

@Composable
private fun LoggedInApp(
    user: UserDto,
    currentPage: NavPage,
    billingCheckoutSuccess: com.checkin.host.ui.billing.BillingCheckoutReturn?,
    sessionExpired: Boolean,
    onNavigate: (NavPage) -> Unit,
    onLogout: () -> Unit,
    onUserUpdated: (UserDto) -> Unit,
    onCheckoutReturnConsumed: () -> Unit,
) {
    LaunchedEffect(user.id) {
        resetViewportAfterNavigation()
    }

    LaunchedEffect(billingCheckoutSuccess) {
        if (billingCheckoutSuccess != null) {
            clearBillingQueryParams()
        }
    }

    AppShell(
        user = user,
        current = currentPage,
        onNavigate = onNavigate,
        onLogout = onLogout,
    ) {
        if (sessionExpired) {
            Div({
                style {
                    padding(12.px)
                    backgroundColor(Color(AppColors.dangerBg))
                    borderRadius(8.px)
                    marginBottom(16.px)
                }
            }) {
                Text("Session expirée. Reconnectez-vous.")
            }
        }

        when (currentPage) {
            NavPage.DASHBOARD -> DashboardScreen()
            NavPage.PROPERTIES -> PropertiesScreen()
            NavPage.RESERVATIONS -> ReservationsScreen()
            NavPage.CONTRACTS -> ContractsScreen(
                user = user,
                onUserUpdated = onUserUpdated,
            )
            NavPage.SUBSCRIPTION -> SubscriptionScreen(
                checkoutReturn = billingCheckoutSuccess,
                onCheckoutReturnConsumed = onCheckoutReturnConsumed,
            )
            NavPage.PROFILE -> ProfileScreen(
                user = user,
                onUserUpdated = onUserUpdated,
            )
        }
    }
}
